'use client';

import { useMemo, useState, type ChangeEvent } from 'react';

type InviteAFriendModalHookParams = {
  eventId: string;
  onOpenChange: (open: boolean) => void;
};

type InputChangeValue =
  | string
  | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

export default function useInviteAFriendModal({
  eventId,
  onOpenChange
}: InviteAFriendModalHookParams) {
  const eventUrl = useMemo(() => {
    const origin =
      typeof window === 'undefined'
        ? process.env.NEXT_PUBLIC_VOLUNTEER_URL
        : window.location.origin;

    if (!origin) return '';

    const url = new URL('/events', origin);
    url.searchParams.set('eventId', eventId);
    return url.toString();
  }, [eventId]);

  const defaultSubject = "Let's volunteer together.";

  const defaultMessage = useMemo(
    () =>
      `Hey! I found this interesting event and thought you might like it. Let's go?\n\n${eventUrl}`,
    [eventUrl]
  );

  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);

  const normalizeChangeValue = (nextValue: InputChangeValue) => {
    if (typeof nextValue === 'string') {
      return nextValue;
    }

    return nextValue.target?.value ?? '';
  };

  const handleSubjectChange = (nextValue: InputChangeValue) => {
    setSubject(normalizeChangeValue(nextValue));
  };

  const handleMessageChange = (nextValue: InputChangeValue) => {
    setMessage(normalizeChangeValue(nextValue));
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSubject(defaultSubject);
      setMessage(defaultMessage);
    }

    onOpenChange(open);
  };

  const mailtoHref = useMemo(() => {
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(message);
    return `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
  }, [subject, message]);

  return {
    subject,
    message,
    handleSubjectChange,
    handleMessageChange,
    handleOpenChange,
    mailtoHref
  };
}
